import React, { useMemo } from 'react'
import { PersonLiteDto } from '../../../api/person/models/PersonDto'
import { CandidateReviewStatus } from '../../../api/role/models/CandidateReviewStatus'
import { SharedWithUserLite } from '../../../api/role/models/getSharedWithUsersWithStatus/GetSharedWithUsersWithStatus'
import SharedUserCard from './SharedUserCard'
import { formatPersonName } from '../../../utils/personUtils'

export type TrustedCircleItem = {
    user: SharedWithUserLite
    candidateReviewStatus: CandidateReviewStatus
}

export default function TrustedCircleList({
    sharedWith,
    personById,
}: {
    sharedWith: TrustedCircleItem[]
    personById: Map<string, PersonLiteDto>
}): React.JSX.Element {
    const sortedShared = useMemo(() => {
        const weight = (s: CandidateReviewStatus) =>
            s === CandidateReviewStatus.COMPLETE
                ? 0
                : s === CandidateReviewStatus.IN_PROGRESS
                  ? 1
                  : 2
        const displayName = (u: SharedWithUserLite) => {
            const p = u.personId ? personById.get(u.personId) : undefined
            if (p) return formatPersonName(p)
            return u.name ?? u.email ?? ''
        }
        return [...sharedWith].sort((a, b) => {
            const aw = weight(a.candidateReviewStatus)
            const bw = weight(b.candidateReviewStatus)
            if (aw !== bw) return aw - bw
            return displayName(a.user).localeCompare(
                displayName(b.user),
                undefined,
                {
                    sensitivity: 'base',
                },
            )
        })
    }, [sharedWith, personById])

    if (sortedShared.length === 0) {
        return (
            <div className="p-6 border rounded-xl bg-white text-center">
                <div className="text-gray-700">
                    Invite your trusted team mates to help you find the right
                    candidate
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col w-full mt-2 gap-2">
            {sortedShared.map((item) => {
                const person = item.user.personId
                    ? personById.get(item.user.personId)
                    : undefined
                const nameFallback = item.user.name ?? item.user.email ?? ''
                return (
                    <SharedUserCard
                        key={item.user.id}
                        person={person}
                        nameFallback={nameFallback}
                        status={item.candidateReviewStatus}
                    />
                )
            })}
        </div>
    )
}
